import { forwardRef, type ReactNode, type Ref, type ButtonHTMLAttributes } from 'react';
import type { BaseProps } from '../../theme/index.js';
import { useBaseAttrs, splitBaseProps } from '../_internal/factory.js';
import { Loader } from '../Loader/Loader.js';
import './ActionIcon.css';

export interface ActionIconProps
  extends BaseProps,
    Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  variant?: 'filled' | 'light' | 'outline' | 'subtle' | 'transparent' | 'default' | 'gradient';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number;
  children?: ReactNode;
}

const SIZE_PX = { xs: 22, sm: 28, md: 34, lg: 40, xl: 48 } as const;

function dim(s: ActionIconProps['size']): number {
  if (s === undefined) return SIZE_PX.md;
  if (typeof s === 'number') return s;
  return SIZE_PX[s as keyof typeof SIZE_PX] ?? SIZE_PX.md;
}

/**
 * Square icon-only button.
 *
 * `onClick` used to be dropped here: the element spread `base.attrs` (an
 * allowlist of `data-` attributes) and nothing else, so every handler passed to
 * an ActionIcon or a CloseButton went nowhere. That is why the close ✕ on a
 * Drawer never closed it while the Cancel button beside it did.
 * `splitBaseProps` now hands back the DOM half of the props.
 */
export const ActionIcon = forwardRef(function ActionIcon(
  { variant = 'subtle', size = 'md', children, loading, disabled, type = 'button', hover, ...rest }: ActionIconProps,
  ref: Ref<HTMLButtonElement>
): ReactNode {
  const props = { ...rest, loading, disabled, hover: hover ?? 'lift' };
  const base = useBaseAttrs('rr-action-icon', props, [`rr-action-icon--${variant}`]);
  const { dom } = splitBaseProps(props);
  const px = dim(size);
  return (
    <button
      {...dom}
      {...base.attrs}
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={base.className}
      style={{ ...base.style, width: px, height: px }}
    >
      {loading ? <Loader size="xs" variant="oval" /> : children}
    </button>
  );
});
